import classNames from 'classnames';
import React from 'react';
import InfoBubble from '../common/infoBubble/infoBubble';
import s from './settingsComponent.module.scss';

interface Props {
  /**
   * What the setting is, phrased so it stays true either way - the switch
   * beside it says which way it is set.
   *
   * These used to be buttons whose label flipped ("Enable"/"Disable playlist
   * status bar"), which meant the text was both the state and the action and
   * read as neither.
   */
  label: string;
  /** One sentence on what it does, when the label cannot carry it. */
  help?: React.ReactNode;
  checked: boolean;
  onChange(): void;
}

/**
 * One on/off setting: static text, then a switch showing which it is.
 *
 * A real checkbox does the work - it is focusable, it takes space and enter,
 * and a screen reader calls it a switch - and everything visible is drawn
 * from its :checked state, so the two can never disagree.
 */
export default function ToggleSetting({ label, help, checked, onChange }: Props) {
  return (
    <div className={classNames(s.setting, s.toggleSetting)}>
      <label className={s.toggleRow}>
        {/*
          The checkbox is the switch: appearance:none and the track drawn on
          the input itself, rather than a hidden input beside a span pretending
          to be one. One element to click, focus and read, and no way for the
          picture to disagree with the state.
        */}
        <input
          type="checkbox"
          role="switch"
          className={s.toggleSwitch}
          checked={checked}
          onChange={onChange}
        />
        {/* The input already announces on/off; this is for the eye. */}
        <span className={s.toggleState} aria-hidden="true">
          {checked ? 'On' : 'Off'}
        </span>
        <span className={s.toggleLabel}>{label}</span>
      </label>
      {help && (
        // Beside the switch rather than under it: a click on the (i) is
        // outside the label, so it neither flips the switch nor reads as a
        // second line of the setting.
        <InfoBubble label={`About "${label}"`}>
          {typeof help === 'string' ? <p>{help}</p> : help}
        </InfoBubble>
      )}
    </div>
  );
}
